import BenchlingConfig from 'BenchlingConfig';

import type {ModelHubPage} from './ModelHubPages';

/**
 * Discriminated union describing the standalone Model Hub view rendered for the current URL. Each
 * variant maps one-to-one to a canonical route (see the path builders below); `ModelHubApp` is a pure
 * function of this value.
 */
export type ModelHubRoute =
  | {kind: 'models'}
  | {kind: 'new-prediction'; model?: string}
  // The predictions listing's batch filter lives in the query string (?predictionBatchId=), which
  // PredictionsPage reads directly, so it is intentionally not part of this route object.
  | {kind: 'predictions'}
  | {kind: 'prediction-detail'; id: string}
  | {kind: 'batches'}
  | {kind: 'batch-detail'; id: string}
  // Read-only account/settings page reached from the standalone sidebar profile menu. Session-gated,
  // and standalone-only: core has its own user settings and never routes here (see accountPath).
  | {kind: 'account'}
  // Signed-out signup wizard takeover. Served at /signup by the backend shell for standalone
  // tenants; renders outside the authenticated app chrome (see ModelHubApp).
  | {kind: 'signup'};

// Path builders — all return leading-slash absolute paths suitable for navigateToURL / Backbone.

export function modelHubBasePath(): string {
  return BenchlingConfig.MODEL_HUB_IS_STANDALONE ? '' : '/model-hub';
}

export function modelsPath(): string {
  return `${modelHubBasePath()}/models`;
}

export function newPredictionPath(model?: string): string {
  return model != null && model !== ''
    ? `${modelHubBasePath()}/new-prediction?model=${encodeURIComponent(model)}`
    : `${modelHubBasePath()}/new-prediction`;
}

export function predictionsPath(batchFilterId?: string): string {
  return batchFilterId != null && batchFilterId !== ''
    ? `${modelHubBasePath()}/predictions?predictionBatchId=${encodeURIComponent(batchFilterId)}`
    : `${modelHubBasePath()}/predictions`;
}

export function predictionDetailPath(id: string): string {
  return `${modelHubBasePath()}/predictions/${encodeURIComponent(id)}`;
}

export function batchesPath(): string {
  return `${modelHubBasePath()}/prediction-batches`;
}

export function batchDetailPath(id: string): string {
  return `${modelHubBasePath()}/prediction-batches/${encodeURIComponent(id)}`;
}

/**
 * Deliberately not base-path aware, like `signupPath`. The account page is reached only from
 * `ModelHubProfileSection` inside the standalone sidebar, which core does not render, so there is no
 * `/model-hub/account` route to build a URL for.
 */
export function accountPath(): string {
  return '/account';
}

export function signupPath(): string {
  return '/signup';
}

type RoutePathBuilder = {
  [Kind in ModelHubRoute['kind']]: (route: Extract<ModelHubRoute, {kind: Kind}>) => string;
};

const ROUTE_PATH_BUILDERS: RoutePathBuilder = {
  models: () => modelsPath(),
  'new-prediction': (route) => newPredictionPath(route.model),
  predictions: () => predictionsPath(),
  'prediction-detail': (route) => predictionDetailPath(route.id),
  batches: () => batchesPath(),
  'batch-detail': (route) => batchDetailPath(route.id),
  account: () => accountPath(),
  signup: () => signupPath(),
};

function buildPathForRoute<Kind extends ModelHubRoute['kind']>(
  route: Extract<ModelHubRoute, {kind: Kind}>
): string {
  return ROUTE_PATH_BUILDERS[route.kind](route);
}

/** Maps any model-hub route object to its canonical path. */
export function pathForRoute(route: ModelHubRoute): string {
  return buildPathForRoute(route);
}

/**
 * Which navbar item should be highlighted for a given route. Detail views highlight their parent
 * listing; the new-prediction takeover highlights nothing (null).
 */
export function activePageForRoute(route: ModelHubRoute): ModelHubPage | null {
  if (route.kind === 'models') {
    return 'models';
  }
  if (route.kind === 'predictions' || route.kind === 'prediction-detail') {
    return 'predictions';
  }
  if (route.kind === 'batches' || route.kind === 'batch-detail') {
    return 'batches';
  }
  // new-prediction and signup are full-screen takeovers, and account is reached from the profile
  // menu — none highlight a nav item.
  return null;
}

function pagePaths(): Record<ModelHubPage, string> {
  return {
    models: modelsPath(),
    predictions: predictionsPath(),
    batches: batchesPath(),
    'new-prediction': newPredictionPath(),
  };
}

/** Maps a navbar page key to its canonical path (used by ModelHubNavbar's onNavigate). */
export function pathForPage(page: ModelHubPage): string {
  return pagePaths()[page];
}
